import { FC, useCallback } from 'react'
import { useMutation } from '@apollo/client'
import { FORM_ERROR } from 'final-form'
import _ from 'lodash'

// types
import type { ReauthModalOptions } from 'contexts/reducers/ui'

// components
import MFACodeForm, {
  MFACodeFormProps,
} from 'components/login/LoginForm/MFACodeForm'

// utils
import { getMutationErrorMessage } from 'helpers/graphql'

// constants
import { COMPLETE_AUTH_CHALLENGE } from 'app/MissionControlStudio/apollo/auth/mutations'

// types
import { AuthChallengeType, MutationError } from 'types/graphql'
import { formatMFAErrorMessage } from 'components/login/LoginForm/ConfirmSignIn/utils'

const DEFAULT_ERROR_MESSAGE = 'Failed to complete the code check'

const ReauthModalMFAStep: FC<{
  authChallengeType: AuthChallengeType
  onComplete: ReauthModalOptions['onComplete']
  onClose: () => void
}> = ({ authChallengeType, onComplete, onClose }) => {
  const [completeAuthChallenge] = useMutation(COMPLETE_AUTH_CHALLENGE)

  const onSubmit = useCallback<MFACodeFormProps['onSubmit']>(
    async values => {
      const { code } = values

      try {
        const { data, errors } = await completeAuthChallenge({
          variables: {
            input: {
              code,
              mfaType: authChallengeType,
            },
          },
        })

        const { session } = data?.completeAuthChallenge ?? {}
        const { reauthInfo } = session ?? {}

        // We expect reauthInfo to be empty if the challenge was completed successfully
        if (!session || !_.isEmpty(reauthInfo)) {
          const mutationErrorMessage =
            getMutationErrorMessage(errors as MutationError[]) ??
            DEFAULT_ERROR_MESSAGE

          const errorMessage = formatMFAErrorMessage({
            error: new Error(mutationErrorMessage),
            loginEvent: authChallengeType,
          })

          return { [FORM_ERROR]: errorMessage }
        }

        onComplete()
        onClose()
      } catch (error) {
        return {
          [FORM_ERROR]: formatMFAErrorMessage({
            error,
            loginEvent: authChallengeType,
          }),
        }
      }

      return null
    },
    [authChallengeType, completeAuthChallenge, onComplete, onClose]
  )

  return (
    <div className='pt-4'>
      <MFACodeForm
        authChallengeType={authChallengeType}
        submitButtonText='Submit'
        showRememberDevice={false}
        onSubmit={onSubmit}
      />
    </div>
  )
}

export default ReauthModalMFAStep
